import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchPortfolioProfiles,
  migratePortfolioProfiles,
  savePortfolioProfile,
  removePortfolioProfile,
} from '../services/portfolioService';
import { getLegacyPortfolioMembers } from '../utils/portfolio-storage';

export const DEFAULT_FAMILY_MEMBERS = {
  self: { id: 'self', name: 'My Portfolio', displayName: 'You', customizable: false, icon: 'Person', color: 'var(--accent)' },
  family: { id: 'family', name: 'Family Portfolio', displayName: 'Combined', customizable: false, icon: 'Groups', color: 'var(--badge-text)' },
};

const readStorage = (key) => {
  try { return localStorage.getItem(key); } catch (_) { return null; }
};
const writeStorage = (key, value) => {
  // Account storage is authoritative; a full/disabled browser cache must not
  // turn a confirmed server save into an apparent failure.
  try { localStorage.setItem(key, value); } catch (_) { /* Cache is optional. */ }
};
const parseMembers = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) { return {}; }
};
const toMembers = (profiles) => ({
  ...DEFAULT_FAMILY_MEMBERS,
  ...Object.fromEntries(profiles
    .filter(member => member?.id && member.id !== 'self' && member.id !== 'family' && !member.deletedAt)
    .map(member => [member.id, member])),
});

export const usePortfolioProfiles = (userId) => {
  const membersKey = `familyMembersSettings:${userId}`;
  const activeKey = `activePortfolio:${userId}`;
  const migrationKey = `portfolioProfilesMigrated:${userId}`;
  const [familyMembers, setFamilyMembers] = useState(() => {
    if (!userId) return DEFAULT_FAMILY_MEMBERS;
    const cached = readStorage(membersKey);
    return toMembers(Object.values(cached ? parseMembers(cached) : getLegacyPortfolioMembers(userId)));
  });
  const [activePortfolio, setActivePortfolio] = useState(() => {
    const saved = userId && (readStorage(activeKey) || (
      Object.keys(getLegacyPortfolioMembers(userId)).length ? readStorage('activePortfolio') : null
    ));
    return saved && familyMembers[saved] ? saved : 'self';
  });
  const [portfoliosLoading, setPortfoliosLoading] = useState(Boolean(userId));
  const [portfolioSyncError, setPortfolioSyncError] = useState('');
  const mounted = useRef(false);
  const queue = useRef(Promise.resolve());
  const refreshing = useRef(null);

  // Serialize reads and writes so a slow initial load cannot erase a new save.
  const enqueue = useCallback((operation) => {
    const result = queue.current.catch(() => {}).then(() => {
      if (!mounted.current) return undefined;
      return operation();
    });
    queue.current = result;
    return result;
  }, []);

  const commitMembers = useCallback((update) => {
    if (!mounted.current) return;
    setFamilyMembers(current => {
      const next = typeof update === 'function' ? update(current) : update;
      writeStorage(membersKey, JSON.stringify(next));
      return next;
    });
  }, [membersKey]);

  const refreshPortfolios = useCallback(() => {
    if (!userId) return Promise.resolve(false);
    if (refreshing.current) return refreshing.current;
    const result = enqueue(async () => {
      setPortfoliosLoading(true);
      try {
        if (readStorage(migrationKey) !== 'true') {
          const legacy = Object.values(getLegacyPortfolioMembers(userId));
          if (legacy.length) {
            writeStorage('familyMembersSettingsOwner', userId);
            await migratePortfolioProfiles(userId, legacy);
          }
          if (!mounted.current) return false;
          writeStorage(migrationKey, 'true');
        }
        const profiles = await fetchPortfolioProfiles(userId);
        if (!mounted.current) return false;
        commitMembers(toMembers(profiles));
        setPortfolioSyncError('');
        return true;
      } catch (_) {
        if (mounted.current) {
          setPortfolioSyncError('Unable to sync portfolios. Check your connection and retry.');
        }
        return false;
      } finally {
        if (mounted.current) setPortfoliosLoading(false);
      }
    });
    refreshing.current = result;
    result.finally(() => { refreshing.current = null; });
    return result;
  }, [userId, migrationKey, enqueue, commitMembers]);

  useEffect(() => {
    mounted.current = true;
    refreshPortfolios();
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshPortfolios();
    };
    window.addEventListener('focus', refreshWhenVisible);
    window.addEventListener('online', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      mounted.current = false;
      window.removeEventListener('focus', refreshWhenVisible);
      window.removeEventListener('online', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshPortfolios]);

  useEffect(() => {
    if (!familyMembers[activePortfolio]) setActivePortfolio('self');
  }, [familyMembers, activePortfolio]);

  useEffect(() => {
    if (userId) writeStorage(activeKey, activePortfolio);
  }, [userId, activeKey, activePortfolio]);

  const requireActiveAccount = () => {
    if (!mounted.current) throw new Error('Your account changed. Reopen portfolio settings to continue.');
  };

  const persistMember = async (member) => {
    if (!userId) throw new Error('Sign in to manage portfolios.');
    requireActiveAccount();
    const saved = await enqueue(async () => {
      const saved = await savePortfolioProfile(userId, member);
      requireActiveAccount();
      commitMembers(current => ({ ...current, [saved.id]: saved }));
      return saved;
    });
    requireActiveAccount();
    return saved;
  };

  const deleteMember = async (memberId) => {
    if (!userId) throw new Error('Sign in to manage portfolios.');
    requireActiveAccount();
    await enqueue(async () => {
      await removePortfolioProfile(userId, memberId);
      requireActiveAccount();
      commitMembers(current => {
        const { [memberId]: removed, ...remaining } = current;
        return remaining;
      });
    });
    requireActiveAccount();
  };

  const switchPortfolio = (portfolioId) => {
    if (familyMembers[portfolioId]) setActivePortfolio(portfolioId);
  };

  return {
    familyMembers, activePortfolio, switchPortfolio, persistMember, deleteMember,
    portfoliosLoading, portfolioSyncError, refreshPortfolios,
  };
};
